import { scheduleOverlapAPI, ScheduleOverlapConflict, ScheduleOverlapResult } from '../services/api';
import { Visit, VisitCreate, VisitParentWithVisits } from '../types';

function isVisitStatusCancelledOrNoShow(status: string | undefined | null): boolean {
  if (status == null || String(status).trim() === '') return false;
  const s = String(status).trim().toLowerCase();
  return s === 'cancelled' || s === 'canceled' || s === 'no-show' || s === 'no show';
}

function intervalsOverlap(
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string
): boolean {
  const t0 = new Date(aStart).getTime();
  const t1 = new Date(aEnd).getTime();
  const t2 = new Date(bStart).getTime();
  const t3 = new Date(bEnd).getTime();
  if (Number.isNaN(t0) || Number.isNaN(t1) || Number.isNaN(t2) || Number.isNaN(t3)) return false;
  if (t0 >= t1 || t2 >= t3) return false;
  return t0 < t3 && t2 < t1;
}

export function buildProviderClientVisitOverlapMessage(conflicts: ScheduleOverlapConflict[]): string {
  const visitConflicts = conflicts.filter((c) => c.source === 'visit');
  if (!visitConflicts.length) return '';
  const lines = visitConflicts.map((c) => {
    const t0 = c.scheduled_start || '?';
    const t1 = c.scheduled_end || '?';
    return `visit #${c.id} (${t0} – ${t1})`;
  });
  return `This visit overlaps with another visit for the same provider and client:\n${lines.join('\n')}`;
}

export function buildOverlapMessageFromConflicts(conflicts: ScheduleOverlapConflict[]): string {
  if (!conflicts.length) return '';
  const lines = conflicts.map((c) => {
    const kind = c.source === 'visit' ? 'visit' : 'transportation';
    const t0 = c.scheduled_start || '?';
    const t1 = c.scheduled_end || '?';
    return `${kind} #${c.id} (${t0} – ${t1})`;
  });
  return `This time overlaps with existing data:\n${lines.join('\n')}`;
}

/** True if two rows in the same save batch have overlapping times for the same client. */
export function findLocalVisitBatchOverlap(
  rows: (VisitCreate & { visit_id?: number })[]
): string | null {
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      const a = rows[i];
      const b = rows[j];
      if (a.client_id !== b.client_id) continue;
      if (isVisitStatusCancelledOrNoShow(a.status) || isVisitStatusCancelledOrNoShow(b.status)) {
        continue;
      }
      if (!a.scheduled_start || !a.scheduled_end || !b.scheduled_start || !b.scheduled_end) {
        continue;
      }
      if (intervalsOverlap(a.scheduled_start, a.scheduled_end, b.scheduled_start, b.scheduled_end)) {
        return 'This visit has multiple lines for the same client with overlapping start/end times. Adjust the times or status before saving.';
      }
    }
  }
  return null;
}

/**
 * For each non-cancelled visit row, check server for overlaps with other visits/transport (same client).
 * When editing, pass existing visit parent fetcher so each line excludes its current visit_id.
 */
export async function runVisitPayloadOverlapCheck(
  desired: (VisitCreate & { visit_id?: number })[],
  options: {
    editingVisit: Visit | null;
    getVisitParent: (id: number) => Promise<VisitParentWithVisits>;
  }
): Promise<string | null> {
  const local = findLocalVisitBatchOverlap(desired);
  if (local) return local;

  let getExcludeVisitId: (index: number) => number | undefined = () => undefined;
  if (options.editingVisit) {
    const parent = await options.getVisitParent(options.editingVisit.visit_parent_id);
    const existing = [...(parent.visits || [])].sort((a, b) => Number(a.visit_id) - Number(b.visit_id));
    getExcludeVisitId = (i) => (existing[i] ? Number(existing[i].visit_id) : undefined);
  }

  for (let i = 0; i < desired.length; i++) {
    const r = desired[i];
    if (isVisitStatusCancelledOrNoShow(r.status)) continue;
    if (!r.scheduled_start || !r.scheduled_end) continue;
    const ex = getExcludeVisitId(i);
    let res: ScheduleOverlapResult;
    try {
      res = await scheduleOverlapAPI.check({
        client_id: Number(r.client_id),
        provider_id: Number(r.provider_id),
        start: r.scheduled_start,
        end: r.scheduled_end,
        exclude_visit_id: ex,
      });
    } catch {
      return null;
    }
    if (res.has_overlap && res.conflicts.length) {
      const msg = buildProviderClientVisitOverlapMessage(res.conflicts);
      if (msg) return msg;
    }
  }
  return null;
}

export async function runSingleVisitOverlapCheck(
  createData: VisitCreate,
  options: { excludeVisitId?: number }
): Promise<string | null> {
  if (isVisitStatusCancelledOrNoShow(createData.status)) return null;
  if (!createData.scheduled_start || !createData.scheduled_end) return null;
  try {
    const res = await scheduleOverlapAPI.check({
      client_id: Number(createData.client_id),
      provider_id: Number(createData.provider_id),
      start: createData.scheduled_start,
      end: createData.scheduled_end,
      exclude_visit_id: options.excludeVisitId,
    });
    if (res.has_overlap && res.conflicts.length) {
      const msg = buildProviderClientVisitOverlapMessage(res.conflicts);
      if (msg) return msg;
    }
  } catch {
    return null;
  }
  return null;
}

export async function runScheduleOverlapWarningCheck(
  params: {
    client_id: number;
    start: string;
    end: string;
    exclude_visit_id?: number;
    exclude_transportation_id?: number;
  }
): Promise<string | null> {
  try {
    const res = await scheduleOverlapAPI.check(params);
    if (res.has_overlap && res.conflicts.length) {
      return buildOverlapMessageFromConflicts(res.conflicts);
    }
  } catch {
    return null;
  }
  return null;
}

type TransportRow = { client_id: number; scheduled_start: string | null; scheduled_end: string | null };

export function findLocalTransportationBatchOverlap(rows: TransportRow[]): string | null {
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      const a = rows[i];
      const b = rows[j];
      if (a.client_id !== b.client_id) continue;
      if (!a.scheduled_start || !a.scheduled_end || !b.scheduled_start || !b.scheduled_end) {
        continue;
      }
      if (intervalsOverlap(a.scheduled_start, a.scheduled_end, b.scheduled_start, b.scheduled_end)) {
        return 'This transportation has multiple lines for the same client with overlapping start/end times.';
      }
    }
  }
  return null;
}

export async function runTransportationRowsOverlapCheck(
  rows: TransportRow[],
  options: {
    getExcludeTransportationId?: (index: number) => number | undefined;
    /** When transportation is linked to a visit, exclude that visit from overlap warnings. */
    getExcludeVisitId?: (index: number) => number | undefined;
  }
): Promise<string | null> {
  const local = findLocalTransportationBatchOverlap(rows);
  if (local) return local;
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (!r.scheduled_start || !r.scheduled_end) continue;
    try {
      const res = await scheduleOverlapAPI.check({
        client_id: Number(r.client_id),
        start: r.scheduled_start,
        end: r.scheduled_end,
        exclude_transportation_id: options.getExcludeTransportationId?.(i),
        exclude_visit_id: options.getExcludeVisitId?.(i),
      });
      if (res.has_overlap && res.conflicts.length) {
        return buildOverlapMessageFromConflicts(res.conflicts);
      }
    } catch {
      return null;
    }
  }
  return null;
}

export { isVisitStatusCancelledOrNoShow };
